import { formatSizeBreakdown, type GuestQuoteView } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatCount, formatDateTime } from '@/shared/lib';

type GuestQuoteSummaryFields = Pick<
  Extract<GuestQuoteView, { state: 'REVIEWING' }>,
  'quoteNumber' | 'productName' | 'colorName' | 'quantity' | 'sizeTotals' | 'requestedAt'
>;

export interface GuestQuoteSummaryProps {
  readonly summary: GuestQuoteSummaryFields;
}

interface SummaryRow {
  readonly label: string;
  readonly value: ReactNode;
}

/** 요청 요약(검토 중·발행됨 공용) — 접수번호·상품·색상·수량·요청일. 연락처·배송지·메모는 응답에 없다(링크 유출 대비). */
export const GuestQuoteSummary = ({ summary }: GuestQuoteSummaryProps): ReactElement => {
  const rows: SummaryRow[] = [
    { label: '접수번호', value: <span className="tabular-nums">{summary.quoteNumber}</span> },
    { label: '상품', value: summary.productName },
    { label: '색상', value: summary.colorName },
    {
      label: '수량',
      value: (
        <span className="tabular-nums">
          {formatCount(summary.quantity)}개
          {summary.sizeTotals.length > 0 ? (
            <span className="ml-2 text-xs text-muted-foreground">
              {formatSizeBreakdown(summary.sizeTotals)}
            </span>
          ) : null}
        </span>
      ),
    },
    { label: '요청일', value: formatDateTime(summary.requestedAt) },
  ];

  return (
    <section aria-label="요청 내용">
      <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm wrap-anywhere">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
            <dd className="min-w-0 flex-1">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
